/**
 * BudgetStoreBridge — pushes Engine document state to Editor store consumers.
 * No business rules. Pure observation + projection callbacks.
 */

import type { BudgetDocument, BudgetEngine } from '@/engines/budget'
import { onBudgetDocumentChange } from '@/engines/budget'

export type BudgetStoreListener = (document: BudgetDocument | null) => void

export class BudgetStoreBridge {
  private listeners = new Set<BudgetStoreListener>()
  private unsubscribeEngine: (() => void) | null = null
  private lastDocument: BudgetDocument | null = null
  private readonly engine: BudgetEngine

  constructor(engine: BudgetEngine) {
    this.engine = engine
  }

  /** Start listening to engine mutations. Idempotent. */
  connect(): void {
    if (this.unsubscribeEngine) return
    this.lastDocument = this.engine.peek()
    this.unsubscribeEngine = onBudgetDocumentChange(this.engine, (document) => {
      this.lastDocument = document
      for (const listener of this.listeners) {
        listener(document)
      }
    })
  }

  disconnect(): void {
    this.unsubscribeEngine?.()
    this.unsubscribeEngine = null
  }

  subscribe(listener: BudgetStoreListener): () => void {
    this.listeners.add(listener)
    listener(this.lastDocument ?? this.engine.peek())
    return () => {
      this.listeners.delete(listener)
    }
  }

  getSnapshot(): BudgetDocument | null {
    return this.lastDocument ?? this.engine.peek()
  }

  /** Notify listeners with current peek (after external restore without event). */
  notify(): void {
    this.lastDocument = this.engine.peek()
    for (const listener of this.listeners) {
      listener(this.lastDocument)
    }
  }
}
